/* eslint-disable no-restricted-syntax */
/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState, useEffect, useCallback } from "react";
import { Settings2, FileText, Calculator, Zap } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { BrowserChrome, AnnotationCallout } from "./primitives";
import { AI_TOOL_COUNT } from "@/content/landing/constants";

const tabs = [
  {
    icon: Settings2,
    title: "Set Up Your Costs",
    description:
      "Align your cost data, set margins and overhead recovery, configure production rates, and build your work packages. Bad data in, bad results out — CC helps you get it right from the start.",
    features: [
      "Pre-loaded costs for 8 trades",
      "Work packages (assemblies)",
      "Production rates & overhead recovery",
    ],
  },
  {
    icon: FileText,
    title: "Build Your Estimate",
    description:
      "Upload blueprints, take measurements directly off plans, and pull in your work packages. AI reviews your numbers and matches costs from your database. Your estimate is accurate before you send it.",
    features: [
      "Blueprint measurements",
      "Work package integration",
      "AI cost matching & review",
    ],
  },
  {
    icon: Calculator,
    title: "Win the Work",
    description:
      "Turn your estimate into a branded proposal with one click. Send it to your client portal. Track responses, revisions, and approvals — all in one place.",
    features: [
      "3 branded proposal templates",
      "Client portal with approval",
      "Scope of work documents",
    ],
  },
  {
    icon: Zap,
    title: "Run the Job",
    description:
      "Your accepted proposal becomes a project. Track budget vs. actuals, manage services, log time entries, and invoice — with AI helping you stay on top of your numbers the whole way.",
    features: [
      "Projects & services",
      "Budget vs. actuals",
      "Invoicing & AI assistant",
    ],
  },
];

// JSX UI mocks modeled after real app components
function ImportMock() {
  return (
    <div className="bg-background text-[11px]">
      {/* Upload zone */}
      <div className="p-3 border-b border-border/40">
        <div className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-secondary/40 bg-secondary/5">
          <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-secondary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-foreground text-xs">
              HVAC_Takeoff_Phase2.xlsx
            </div>
            <div className="text-muted-foreground text-[10px]">
              142 KB — 24 line items detected
            </div>
          </div>
          <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 flex-shrink-0">
            Imported
          </span>
        </div>
      </div>
      {/* Work area groups */}
      <div className="p-3 space-y-1.5">
        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
          Work Areas
        </div>
        {[
          { area: "Ductwork", items: 12, matched: 12 },
          { area: "Piping", items: 8, matched: 7 },
          { area: "Equipment", items: 4, matched: 2 },
        ].map((g) => (
          <div
            key={g.area}
            className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/30"
          >
            <div className="flex items-center gap-2">
              <span className="text-foreground font-medium">{g.area}</span>
              <span className="text-[10px] text-muted-foreground">
                {g.items} items
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              {/* Mini progress bar */}
              <div className="w-12 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-green-500"
                  style={{ width: `${(g.matched / g.items) * 100}%` }}
                />
              </div>
              <span className="text-[10px] text-muted-foreground tabular-nums">
                {g.matched}/{g.items}
              </span>
            </div>
          </div>
        ))}
      </div>
      {/* Footer */}
      <div className="px-3 py-2 border-t border-border/40 flex justify-between text-[10px] text-muted-foreground">
        <span>24 total line items</span>
        <span className="text-green-600 dark:text-green-400 font-medium">
          21 matched
        </span>
      </div>
    </div>
  );
}

function ProjectBudgetMock() {
  const rows = [
    { name: "Materials", budget: 12400, actual: 11850, status: "on-track" },
    { name: "Labor", budget: 8600, actual: 9100, status: "over" },
    { name: "Equipment", budget: 3200, actual: 2800, status: "under" },
    { name: "Subcontractors", budget: 6000, actual: 5500, status: "under" },
  ];
  const totalBudget = rows.reduce((s, r) => s + r.budget, 0);
  const totalActual = rows.reduce((s, r) => s + r.actual, 0);
  return (
    <div className="bg-background text-[11px]">
      {/* Header */}
      <div className="p-3 border-b border-border/40">
        <div className="flex items-center justify-between mb-2">
          <span className="font-semibold text-foreground text-xs">
            Greenfield HVAC — Phase 2
          </span>
          <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            In Progress
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[10px]">
          <div>
            <div className="text-muted-foreground">Budget</div>
            <div className="text-foreground font-semibold">
              ${totalBudget.toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Spent</div>
            <div className="text-foreground font-semibold">
              ${totalActual.toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Remaining</div>
            <div className="text-green-600 dark:text-green-400 font-semibold">
              ${(totalBudget - totalActual).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
      {/* Line items */}
      <div className="grid grid-cols-[1fr_60px_60px_52px] gap-px px-3 py-1.5 bg-muted/40 border-b border-border/40 text-[9px] font-medium text-muted-foreground uppercase tracking-wider">
        <span>Category</span>
        <span className="text-right">Budget</span>
        <span className="text-right">Actual</span>
        <span className="text-right">Var</span>
      </div>
      {rows.map((row, i) => {
        const variance = ((row.actual - row.budget) / row.budget) * 100;
        return (
          <div
            key={row.name}
            className={`grid grid-cols-[1fr_60px_60px_52px] gap-px px-3 py-1.5 border-b border-border/20 ${i % 2 === 1 ? "bg-muted/15" : ""}`}
          >
            <span className="text-foreground font-medium">{row.name}</span>
            <span className="text-right tabular-nums">
              ${row.budget.toLocaleString()}
            </span>
            <span className="text-right tabular-nums">
              ${row.actual.toLocaleString()}
            </span>
            <span
              className={`text-right tabular-nums font-medium ${
                variance > 0
                  ? "text-red-600 dark:text-red-400"
                  : "text-green-600 dark:text-green-400"
              }`}
            >
              {variance > 0 ? "+" : ""}
              {variance.toFixed(1)}%
            </span>
          </div>
        );
      })}
      <div className="px-3 py-2 flex justify-between text-[10px]">
        <span className="text-muted-foreground">4 categories</span>
        <span className="text-green-600 dark:text-green-400 font-medium">
          ${(totalBudget - totalActual).toLocaleString()} under budget
        </span>
      </div>
    </div>
  );
}

function AskHankMock() {
  return (
    <div className="bg-background text-[11px]">
      {/* Chat header */}
      <div className="px-3 py-2 border-b border-border/40 flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-secondary/10 flex items-center justify-center text-[9px] text-secondary font-bold">
          AI
        </div>
        <span className="font-semibold text-foreground text-xs">
          Hank — AI Assistant
        </span>
        <span className="text-[10px] text-muted-foreground ml-auto">
          {AI_TOOL_COUNT} tools available
        </span>
      </div>
      {/* Chat messages */}
      <div className="p-3 space-y-3">
        {/* User message */}
        <div className="flex justify-end">
          <div className="bg-secondary/10 rounded-lg rounded-br-sm px-3 py-2 max-w-[80%]">
            <p className="text-foreground">
              What&apos;s my average margin on HVAC jobs this year?
            </p>
          </div>
        </div>
        {/* AI response */}
        <div className="flex justify-start">
          <div className="bg-muted/50 rounded-lg rounded-bl-sm px-3 py-2 max-w-[85%] space-y-2">
            <p className="text-foreground">
              Across 12 HVAC estimates in 2026, your average margin is{" "}
              <span className="font-semibold text-secondary">22.4%</span>.
            </p>
            <div className="grid grid-cols-3 gap-2 text-[10px] pt-1 border-t border-border/30">
              <div>
                <div className="text-muted-foreground">Highest</div>
                <div className="text-foreground font-medium">31.2%</div>
              </div>
              <div>
                <div className="text-muted-foreground">Lowest</div>
                <div className="text-foreground font-medium">14.8%</div>
              </div>
              <div>
                <div className="text-muted-foreground">Jobs</div>
                <div className="text-foreground font-medium">12</div>
              </div>
            </div>
            <p className="text-muted-foreground text-[10px]">
              Your top performer was the Greenfield Phase 2 job at 31.2%. Lowest
              was the Johnson Residence retrofit.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CostSetupMock() {
  const categories = [
    { name: "Materials", items: 42, status: "configured" },
    { name: "Labor Tasks", items: 18, status: "configured" },
    { name: "Equipment", items: 8, status: "configured" },
    { name: "Work Packages", items: 12, status: "configured" },
  ];
  return (
    <div className="bg-background text-[11px]">
      <div className="px-3 py-2 border-b border-border/40 flex items-center justify-between">
        <span className="font-semibold text-foreground text-xs">
          Cost Database — Landscaping
        </span>
        <span className="text-[10px] text-muted-foreground">80 items</span>
      </div>
      {categories.map((cat, i) => (
        <div
          key={cat.name}
          className={`flex items-center justify-between px-3 py-2 border-b border-border/20 ${i % 2 === 1 ? "bg-muted/15" : ""}`}
        >
          <span className="text-foreground font-medium">{cat.name}</span>
          <div className="flex items-center gap-3">
            <span className="text-muted-foreground tabular-nums">
              {cat.items} items
            </span>
            <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
              Ready
            </span>
          </div>
        </div>
      ))}
      <div className="px-3 py-2 text-[10px] text-muted-foreground flex justify-between">
        <span>Overhead: 18% | Margin: 22%</span>
        <span className="text-green-600 dark:text-green-400 font-medium">
          All categories configured
        </span>
      </div>
    </div>
  );
}

// Tab 1: Cost Setup, Tab 2: Build Estimate (import/measure), Tab 3: Win Work (proposal), Tab 4: Run Job (budget)
const tabContent = [CostSetupMock, ImportMock, AskHankMock, ProjectBudgetMock];

export function ProductShowcaseSection() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  // Auto-advance
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % tabs.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [paused, active]);

  const handleTabClick = useCallback((index: number) => {
    setActive(index);
    setPaused(true);
  }, []);

  const ActiveMock = tabContent[active] as React.ComponentType;

  return (
    <section
      className="py-16 md:py-24 bg-background-warm-alt"
      aria-labelledby="showcase-heading"
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
        {/* Header */}
        <header className="mb-6 md:mb-10 max-w-3xl">
          <span className="section-badge mb-4">How It Works</span>
          <h2
            id="showcase-heading"
            className="text-[clamp(24px,3.5vw,36px)] leading-[1.1] font-bold text-foreground mb-3"
          >
            Set up once.{" "}
            <span className="text-highlight">Estimate forever.</span>
          </h2>
          <p className="text-base text-muted-foreground">
            Configure your costs, build estimates from blueprints, win the work,
            run the job — one connected flow.
          </p>
        </header>

        {/* Tabbed layout */}
        <div
          className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
        >
          {/* Tab list */}
          <div className="lg:col-span-4 flex flex-row lg:flex-col gap-2">
            {tabs.map((tab, i) => {
              const Icon = tab.icon;
              const isActive = i === active;
              return (
                <button
                  key={tab.title}
                  onClick={() => handleTabClick(i)}
                  className={`flex-1 lg:flex-none text-left p-3 md:p-4 rounded-lg border-l-2 transition-all duration-200 ${
                    isActive
                      ? "bg-secondary/10 border-l-secondary"
                      : "bg-transparent border-l-transparent hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-1">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                        isActive ? "bg-secondary/15" : "bg-muted/50"
                      }`}
                    >
                      <Icon
                        className={`w-4 h-4 ${isActive ? "text-secondary" : "text-muted-foreground"}`}
                      />
                    </div>
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                      Step {i + 1}
                    </span>
                  </div>
                  <h3 className="font-semibold text-foreground text-sm mb-1 hidden lg:block">
                    {tab.title}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed hidden lg:block">
                    {tab.description}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Content panel */}
          <div className="lg:col-span-8 relative">
            <BrowserChrome>
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <ActiveMock />
                </motion.div>
              </AnimatePresence>
            </BrowserChrome>

            {/* Blueprint annotation on budget tab */}
            {active === 1 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.3 }}
              >
                <AnnotationCallout
                  label="Under budget:"
                  value="$950"
                  className="-bottom-3 -right-2 md:-right-6"
                />
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
